<template>
  <link rel="stylesheet" href="/assets/css/timeline-theme.css">
  <link rel="stylesheet" href="/assets/css/timeline-mobile.css">
  <main id="main" class="main">
    <section class="section">
      <div class="row">
        <div class="col-lg-12">
          <div>
            <div>
              <!-- Page Title & Breadcrumb -->
              <div class="row mb-2">
                <div class="col-sm-9">
                  <div class="pagetitle">
                    <div class="d-flex align-items-center gap-3 flex-wrap">
                      <h1 class="text-left text-uppercase mb-0" :style="{ color: themeColor }">Instance Timeline</h1>
                      <div class="google-stat-badge fade-in py-1 px-3 d-flex align-items-center gap-2" :style="{ '--primary-theme-color': themeColor, borderRadius: '8px', fontSize: '0.85rem' }">
                        <div class="badge-icon" :style="{ backgroundColor: themeColor + '15', color: themeColor, width: '24px', height: '24px', fontSize: '0.8rem' }">
                          <i class="bi bi-activity"></i>
                        </div>
                        <div class="d-flex align-items-center gap-1">
                          <span class="text-muted fw-bold" style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px;">Total Events:</span>
                          <span class="badge-value fw-extrabold" :style="{ color: themeColor, fontSize: '0.9rem' }">{{ animatedEventCount }}</span>
                        </div>
                      </div>
                    </div>
                    <nav class="mt-2">
                      <ol class="breadcrumb breadJa">
                        <li class="breadcrumb-item">
                          <router-link :to="{ name: 'home' }">Home</router-link>
                        </li>
                        <router-link :to="{ name: 'timeline' }" class="breadcrumb-item">Timeline</router-link>
                      </ol>
                    </nav>
                  </div>
                </div>
              </div>

              <!-- Google Standard Unified Toolbar -->
              <div class="google-toolbar mb-3">
                <div class="d-flex align-items-stretch gap-2 gap-lg-3 flex-wrap flex-lg-nowrap">
                  <!-- Instance Settings Section -->
                  <div class="toolbar-section toolbar-section-primary flex-shrink-0">
                    <div class="section-header mb-2">
                      <i class="bi bi-gear-fill me-1"></i>
                      <span>Pengaturan Instance</span>
                    </div>
                    <div class="d-flex align-items-center gap-3 flex-wrap">
                      <!-- Instance Selection -->
                      <div class="d-flex align-items-center gap-2">
                        <label class="toolbar-label">
                          <i class="bi bi-hdd-network me-1"></i> Instance
                        </label>
                        <select 
                          v-model="selectedInstanceId" 
                          @change="onInstanceChange" 
                          class="toolbar-select"
                          :disabled="loadingInstances || (isAdmin && showAll)"
                        >
                          <option value="none">Pilih Instance Unit</option>
                          <option 
                            v-for="instance in instances" 
                            :key="instance.instanceId" 
                            :value="instance.instanceId"
                          >
                            {{ instance.phoneNumber }} - {{ instance.instanceId.substring(0, 8) }}
                          </option>
                        </select>
                      </div>

                      <!-- Admin Toggle -->
                      <div v-if="isAdmin" class="google-switch-wrapper">
                        <input 
                          class="form-check-input google-switch" 
                          type="checkbox" 
                          role="switch" 
                          id="showAllSwitch"
                          v-model="showAll"
                          @change="onToggleAll"
                        >
                        <label class="form-check-label toolbar-label" for="showAllSwitch">
                          Tampilkan Semua
                        </label>
                      </div>
                    </div>
                  </div>

                  <div class="v-separator-thick"></div>

                  <!-- Attendance Filters Section -->
                  <div class="toolbar-section toolbar-section-secondary flex-grow-1">
                    <div class="section-header mb-2">
                      <i class="bi bi-funnel-fill me-1"></i>
                      <span>Filter Kehadiran</span>
                    </div>
                    <div class="d-flex align-items-center gap-2 gap-lg-3 flex-wrap flex-lg-nowrap">
                      <!-- Date Range Filter -->
                      <div class="d-flex align-items-center gap-2">
                        <label class="toolbar-label">
                          <i class="bi bi-calendar-range me-1"></i> Tanggal
                        </label>
                        <input 
                          type="date" 
                          v-model="attendanceFilters.startDate" 
                          class="toolbar-select" 
                          style="min-width: 130px;"
                        >
                        <span class="text-muted">—</span>
                        <input 
                          type="date" 
                          v-model="attendanceFilters.endDate" 
                          class="toolbar-select" 
                          style="min-width: 130px;"
                        >
                      </div>

                      <!-- Category Filter -->
                      <div class="d-flex align-items-center gap-2">
                        <label class="toolbar-label">
                          <i class="bi bi-tag me-1"></i> Kategori
                        </label>
                        <select 
                          v-model="attendanceFilters.attendanceType" 
                          class="toolbar-select"
                          style="min-width: 150px;"
                        >
                          <option value="">Semua Kategori</option>
                          <option value="device_login">Masuk Perangkat</option>
                          <option value="sim_change">Ganti SIM Card</option>
                          <option value="maintenance">Perbaikan Rutin</option>
                          <option value="signal_check">Pengecekan Sinyal</option>
                          <option value="battery_swap">Tukar Baterai</option>
                          <option value="relocation">Pindah Lokasi</option>
                          <option value="system_update">Update Sistem</option>
                          <option value="data_backup">Backup Data</option>
                          <option value="emergency">Keadaan Darurat</option>
                          <option value="other">Lainnya</option>
                        </select>
                      </div>

                      <!-- Action Buttons Group -->
                      <div class="d-flex align-items-center gap-2">
                        <!-- Apply Button -->
                        <button 
                          class="btn btn-sm text-white px-3 d-flex align-items-center justify-content-center" 
                          @click="applyAttendanceFilters"
                          :style="{ 
                            backgroundColor: themeColor, 
                            borderColor: themeColor, 
                            borderRadius: '12px', 
                            height: '38px',
                            fontWeight: '600',
                            fontSize: '0.85rem',
                            boxShadow: '0 1px 2px rgba(60,64,67,0.3)'
                          }"
                        >
                          <i class="bi bi-check-circle me-1"></i> Terapkan
                        </button>

                        <!-- Reset Button -->
                        <button 
                          class="btn btn-sm btn-outline-secondary px-3 d-flex align-items-center justify-content-center" 
                          @click="resetAttendanceFilters"
                          v-if="appliedFilters.startDate || appliedFilters.endDate || appliedFilters.attendanceType || attendanceFilters.startDate || attendanceFilters.endDate || attendanceFilters.attendanceType"
                          :style="{ 
                            borderRadius: '12px', 
                            height: '38px',
                            fontWeight: '600',
                            fontSize: '0.85rem',
                            color: '#5f6368',
                            borderColor: '#dadce0',
                            backgroundColor: 'transparent'
                          }"
                        >
                          <i class="bi bi-x-circle me-1"></i> Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Loading State -->
              <Transition name="fade" mode="out-in">
                <div v-if="loading" key="loading" class="text-center py-5">
                  <div class="spinner-border text-primary" role="status" :style="{ color: themeColor + ' !important' }">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-3 text-muted">Loading timeline events...</p>
                </div>

                <!-- Error State -->
                <div v-else-if="error" key="error" class="alert alert-danger d-flex align-items-center" role="alert">
                  <i class="bi bi-exclamation-triangle me-2" style="font-size: 1.5rem;"></i>
                  <div>{{ error }}</div>
                </div>

                <!-- Calendar View -->
                <div v-else-if="shouldShowCalendar" key="calendar" class="calendar-container">
                  <FullCalendar :options="calendarOptions" />
                </div>

                <!-- Initial State (Lottie) -->
                <div v-else key="empty" class="text-center py-4">
                  <div class="illustration-container">
                    <Vue3Lottie  
                      animationLink="/assets/Contact.json"
                      :height="240" 
                      :width="240"
                      :loop="true"
                      :autoPlay="true"
                    />
                  </div>
                  <div class="mt-2">
                    <h6 class="fw-bold text-dark">Siap Melihat Catatan?</h6>
                    <p class="text-muted small">Pilih opsi di atas untuk menampilkan timeline & absensi</p>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Event Detail Modal (System Events) -->
    <div class="modal fade" id="eventDetailModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content modal-google-premium shadow-lg" :style="{ '--primary-theme-color': themeColor }">
          <div class="modal-google-header d-flex align-items-center justify-content-between">
            <h5 class="modal-google-title d-flex align-items-center mb-0">
              <div class="modal-google-icon me-3">
                <i class="bi bi-info-circle-fill" :style="{ color: themeColor }"></i>
              </div>
              Event Details
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4" v-if="selectedEvent">
            <div class="google-form-group">
              <label class="google-input-label">Event Type</label>
              <div class="google-input-wrapper d-flex align-items-center">
                <div class="d-flex align-items-center">
                  <span class="badge rounded-pill fw-bold" :style="{ backgroundColor: themeColor + '15', color: themeColor, fontSize: '12px', padding: '0.4rem 1rem' }">
                    <i class="bi bi-tag-fill me-1"></i>
                    {{ selectedEvent.extendedProps?.eventType || '-' }}
                  </span>
                </div>
              </div>
            </div>
            
            <div class="google-form-group">
              <label class="google-input-label">Date & Time</label>
              <div class="google-input-wrapper">
                <i class="bi bi-calendar3 me-2"></i>
                <span class="fw-semibold">{{ formatEventDate(selectedEvent.start) }}</span>
              </div>
            </div>

            <div class="google-form-group mb-0">
              <label class="google-input-label">Description</label>
              <div class="google-input-wrapper">
                <span>{{ selectedEvent.extendedProps?.description || '-' }}</span>
              </div>
            </div>

            <div v-if="selectedEvent.extendedProps?.metadata" class="mt-3">
              <label class="google-input-label">Metadata</label>
              <pre class="bg-light p-3 rounded mt-1 border" style="font-size: 0.8rem; max-height: 200px; overflow-y: auto;">{{ formatMetadata(selectedEvent.extendedProps.metadata) }}</pre>
            </div>
          </div>
          <div class="modal-google-footer d-flex justify-content-end">
            <button type="button" class="btn btn-google-outline" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Attendance Form Modal (Add/Edit) -->
    <div class="modal fade" id="attendanceModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content modal-google-premium shadow-lg" :style="{ '--primary-theme-color': themeColor }">
          <div class="modal-google-header d-flex align-items-center justify-content-between">
            <h5 class="modal-google-title d-flex align-items-center mb-0">
              <div class="modal-google-icon me-3">
                <i class="bi bi-person-check-fill" :style="{ color: themeColor }"></i>
              </div>
              {{ isEditingAttendance ? 'Edit Record Absensi' : 'Tambah Record Absensi' }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="saveAttendance">
            <div class="modal-body p-4">
              <div class="row g-3">
                <!-- Group 1: Identity & Timing -->
                <div class="col-md-4">
                  <div class="google-form-group">
                    <label class="google-input-label">Tanggal</label>
                    <div class="google-input-wrapper d-flex align-items-center">
                      <i class="bi bi-calendar-event me-2"></i>
                      <input type="date" v-model="attendanceForm.attendanceDate" required disabled>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="google-form-group">
                    <label class="google-input-label">Nomor WhatsApp</label>
                    <div class="google-input-wrapper d-flex align-items-center">
                      <i class="bi bi-whatsapp me-2"></i>
                      <select v-model="attendanceForm.phoneNumber" required>
                        <option value="">Pilih Nomor...</option>
                        <option 
                          v-for="instance in instances" 
                          :key="instance.instanceId" 
                          :value="instance.phoneNumber"
                        >
                          {{ instance.phoneNumber }} ({{ instance.instanceId.substring(0,6) }})
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="google-form-group">
                    <label class="google-input-label">Label SIM Card</label>
                    <div class="google-input-wrapper d-flex align-items-center">
                      <i class="bi bi-sim me-2"></i>
                      <input type="text" v-model="attendanceForm.simLabel" placeholder="Contoh: SIM 01">
                    </div>
                  </div>
                </div>

                <!-- Group 2: Activity Details -->
                <div class="col-md-6">
                  <div class="google-form-group">
                    <label class="google-input-label">Kategori Absen</label>
                    <div class="google-input-wrapper d-flex align-items-center">
                      <i class="bi bi-tag me-2"></i>
                      <select v-model="attendanceForm.attendanceType">
                        <option value="device_login">Masuk Perangkat</option>
                        <option value="sim_change">Ganti SIM Card</option>
                        <option value="maintenance">Perbaikan Rutin</option>
                        <option value="signal_check">Pengecekan Sinyal</option>
                        <option value="battery_swap">Tukar Baterai</option>
                        <option value="relocation">Pindah Lokasi</option>
                        <option value="system_update">Update Sistem</option>
                        <option value="data_backup">Backup Data</option>
                        <option value="emergency">Keadaan Darurat</option>
                        <option value="other">Lainnya</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="google-form-group">
                    <label class="google-input-label">Status Unit</label>
                    <div class="google-input-wrapper d-flex align-items-center">
                      <i class="bi bi-activity me-2"></i>
                      <select v-model="attendanceForm.status">
                        <option value="active">Active (Aktif)</option>
                        <option value="inactive">Inactive (Mati)</option>
                        <option value="maintenance">Maintenance (Perbaikan)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="col-md-12">
                  <div class="google-form-group">
                    <label class="google-input-label">Judul Aktivitas</label>
                    <div class="google-input-wrapper d-flex align-items-center">
                      <i class="bi bi-fonts me-2"></i>
                      <input type="text" v-model="attendanceForm.title" placeholder="Contoh: Login ke Device Samsung A50" required>
                    </div>
                  </div>
                </div>

                <div class="col-md-12">
                  <div class="google-form-group mb-0">
                    <label class="google-input-label">Catatan Tambahan</label>
                    <div class="google-input-wrapper d-flex align-items-start">
                      <i class="bi bi-sticky me-2 mt-1"></i>
                      <textarea v-model="attendanceForm.notes" rows="2" placeholder="Tulis catatan di sini..."></textarea>
                    </div>
                  </div>
                </div>

                <div class="col-md-12" v-if="!isEditingAttendance">
                  <div class="google-form-group mb-0 mt-2">
                    <label class="google-input-label">Advanced Metadata (JSON)</label>
                    <div class="google-input-wrapper d-flex align-items-center bg-light bg-opacity-50">
                      <i class="bi bi-braces me-2"></i>
                      <input type="text" v-model="attendanceForm.metadata" placeholder='{"device": "Samsung", "slot": 1}'>
                    </div>
                    <small class="google-help-text">
                      <i class="bi bi-info-circle me-1"></i> Opsional: Masukkan data tambahan dalam format JSON yang valid.
                    </small>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-google-footer d-flex justify-content-between">
              <div v-if="isEditingAttendance">
                <button type="button" class="btn btn-google-danger-outline" @click="confirmDeleteAttendance">
                  <i class="bi bi-trash3 me-1"></i> Hapus
                </button>
              </div>
              <div class="ms-auto">
                <button type="button" class="btn btn-google-outline me-2" data-bs-dismiss="modal">Batal</button>
                <button type="submit" class="btn btn-google-primary">
                  <i class="bi bi-check2-circle me-1"></i> {{ isEditingAttendance ? 'Simpan Perubahan' : 'Rekam Absen' }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import axios from 'axios';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { useSystem } from '@/composables/useSystem.js';
import { useAuth } from '@/composables/useAuth';
import { Vue3Lottie } from 'vue3-lottie';
import lottie from 'lottie-web';
import { computed } from 'vue';

export default {
  name: 'InstanceTimeline',
  components: {
    FullCalendar,
    Vue3Lottie
  },
  setup() {
    const { themeColor } = useSystem();
    const { user } = useAuth();
    const isAdmin = computed(() => user.value?.role === 'admin');
    return { themeColor, isAdmin };
  },
  computed: {
    shouldShowCalendar() {
      return this.selectedInstanceId !== 'none' || 
             this.showAll || 
             !!this.appliedFilters.startDate || 
             !!this.appliedFilters.endDate || 
             !!this.appliedFilters.attendanceType;
    }
  },
  data() {
    return {
      instances: [],
      selectedInstanceId: 'none',
      showAll: false,
      loadingInstances: false,
      loading: false,
      error: null,
      events: [],
      attendances: [],
      attendanceFilters: {
        startDate: '',
        endDate: '',
        attendanceType: ''
      },
      appliedFilters: {
        startDate: '',
        endDate: '',
        attendanceType: ''
      },
      selectedEvent: null,
      selectedAttendance: null,
      isEditingAttendance: false,
      attendanceForm: {
        attendanceDate: '',
        phoneNumber: '',
        simLabel: '',
        attendanceType: 'device_login',
        status: 'active',
        title: '',
        notes: '',
        metadata: ''
      },
      baseUrl: process.env.VUE_APP_BE_APP_BASE_URL,
      animatedEventCount: 0,
      calendarOptions: {
        plugins: [dayGridPlugin, listPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,listMonth'
        },
        events: [],
        eventClick: this.handleEventClick,
        eventContent: this.renderEventContent,
        dateClick: this.handleDateClick,
        height: 700, // Balanced height
        contentHeight: 620,
        dayMaxEvents: 5, // Show more events before "more" link
        moreLinkText: (num) => `+${num}` 
      }
    };
  },
  mounted() {
    this.fetchInstances();
    this.fetchAttendances();
  },
  watch: {
    'calendarOptions.events': {
      handler() {
        this.updateAnimatedCount();
      },
      deep: true
    },
    selectedInstanceId() {
      this.updateAnimatedCount();
    },
    showAll() {
      this.updateAnimatedCount();
    },
    shouldShowCalendar() {
      this.updateAnimatedCount();
    }
  },
  methods: {
    updateAnimatedCount() {
      const targetCount = this.shouldShowCalendar 
        ? (this.calendarOptions.events ? this.calendarOptions.events.length : 0)
        : 0;
      this.animateCount(targetCount);
    },
    
    animateCount(targetValue) {
      const startValue = this.animatedEventCount;
      const duration = 1000; // 1 second animation
      const startTime = performance.now();

      const updateCount = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smoother feel (easeOutExpo)
        const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        
        this.animatedEventCount = Math.floor(startValue + (targetValue - startValue) * easeProgress);

        if (progress < 1) {
          requestAnimationFrame(updateCount);
        }
      };

      requestAnimationFrame(updateCount);
    },

    async fetchInstances() {
      this.loadingInstances = true;
      try {
        const response = await axios.get(`${this.baseUrl}/api/instances`, {
          params: { all: true }
        });
        
        if (response.data.success) {
          this.instances = response.data.data.instances || [];
        }
      } catch (error) {
        console.error('Failed to fetch instances:', error);
        this.showToast('Failed to load instances. Please try again.', 'error');
      } finally {
        this.loadingInstances = false;
      }
    },

    async fetchAttendances() {
      try {
        const params = {};
        
        if (this.appliedFilters.startDate) {
          params.start_date = this.appliedFilters.startDate;
        }
        if (this.appliedFilters.endDate) {
          params.end_date = this.appliedFilters.endDate;
        }
        if (this.appliedFilters.attendanceType) {
          params.attendance_type = this.appliedFilters.attendanceType;
        }
        if (this.selectedInstanceId && this.selectedInstanceId !== 'none') {
          params.instance_id = this.selectedInstanceId;
        }

        const response = await axios.get(`${this.baseUrl}/api/attendance`, { params });
        if (response.data.success) {
          this.attendances = response.data.data.attendances || [];
          this.mergeEvents();
        }
      } catch (error) {
        console.error('Failed to fetch attendances:', error);
      }
    },

    applyAttendanceFilters() {
      this.appliedFilters = { ...this.attendanceFilters };
      this.fetchAttendances();
    },

    resetAttendanceFilters() {
      this.attendanceFilters = {
        startDate: '',
        endDate: '',
        attendanceType: ''
      };
      this.appliedFilters = {
        startDate: '',
        endDate: '',
        attendanceType: ''
      };
      this.fetchAttendances();
    },

    async onInstanceChange() {
      this.showAll = false; // Uncheck "Show All" when specific instance is picked
      await Promise.all([
        this.fetchTimeline(),
        this.fetchAttendances()
      ]);
    },

    async onToggleAll() {
      if (this.showAll) {
        this.selectedInstanceId = 'none'; // Clear specific instance
      }
      await Promise.all([
        this.fetchTimeline(),
        this.fetchAttendances()
      ]);
    },

    async fetchTimeline() {
      this.loading = true;
      this.error = null;
      
      try {
        let endpoint = '';
        if (this.isAdmin && this.showAll) {
          endpoint = `${this.baseUrl}/api/timeline`;
        } else if (this.selectedInstanceId !== 'none') {
          endpoint = `${this.baseUrl}/api/instances/${this.selectedInstanceId}/timeline`;
        } else {
          this.events = [];
          this.mergeEvents();
          this.loading = false;
          return;
        }
          
        const response = await axios.get(endpoint);
        
        if (response.data.success) {
          const timelineData = response.data.data;
          this.events = timelineData.events || [];
          this.mergeEvents();
        } else {
          this.error = response.data.message || 'Failed to load timeline';
        }
      } catch (error) {
        console.error('Failed to fetch timeline:', error);
        this.error = error.response?.data?.message || 'Failed to load timeline. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async refreshAllData() {
      await Promise.all([
        this.fetchAttendances(),
        this.fetchTimeline()
      ]);
    },

    mergeEvents() {
      // Filter out system events that are already captured by the attendance records
      // to avoid "double badges" on the calendar.
      const systemEvents = this.events
        .filter(event => event.eventType !== 'SIM_ATTENDANCE')
        .map(event => {
          const icon = this.getEventIcon(event.eventType);
        const color = this.getEventColor(event.eventType);
        
        return {
          id: `system-${event.id}`,
          title: event.description,
          start: event.eventDate,
          backgroundColor: color,
          borderColor: color,
          extendedProps: {
            eventType: event.eventType,
            description: event.description,
            metadata: event.metadata,
            userId: event.userId,
            icon: icon,
            isSystem: true,
            rawDate: event.eventDate
          },
          display: 'block',
          classNames: ['custom-event', 'system-event']
        };
      });

      const attendanceEvents = this.attendances.map(attn => {
        return {
          id: `attendance-${attn.id}`,
          title: attn.title,
          start: attn.attendanceDate,
          backgroundColor: '#6f42c1', // Purple for attendance
          borderColor: '#6f42c1',
          extendedProps: {
            ...attn,
            icon: 'bi-person-check',
            isSystem: false
          },
          display: 'block',
          classNames: ['custom-event', 'attendance-event']
        };
      });

      this.calendarOptions.events = [...systemEvents, ...attendanceEvents];
    },

    getEventColor(eventType) {
      const colorMap = {
        'INSTANCE_CREATED': '#0d6efd',      // blue
        'USER_ASSIGNED': '#198754',         // green
        'WARMING_ROOM_JOINED': '#fd7e14',   // orange
        'WARMING_ACTIVITY': '#ffc107',      // yellow
      };
      
      // Check if it's an AUDIT_* event
      if (eventType.startsWith('AUDIT_')) {
        return '#6c757d'; // gray
      }
      
      return colorMap[eventType] || '#6c757d'; // default gray
    },

    getEventIcon(eventType) {
      const iconMap = {
        'INSTANCE_CREATED': 'bi-plus-circle-fill',
        'USER_ASSIGNED': 'bi-person-check-fill',
        'WARMING_ROOM_JOINED': 'bi-door-open-fill',
        'WARMING_ACTIVITY': 'bi-chat-dots-fill',
      };
      
      // Check if it's an AUDIT_* event
      if (eventType.startsWith('AUDIT_')) {
        return 'bi-shield-check';
      }
      
      return iconMap[eventType] || 'bi-calendar-event';
    },

    renderEventContent(arg) {
      const icon = arg.event.extendedProps.icon || 'bi-calendar-event';
      const isListView = arg.view.type === 'listMonth';
      
      if (isListView) {
        // List view rendering - show full details
        const arrayOfDomNodes = [];
        const iconEl = document.createElement('i');
        iconEl.className = `bi ${icon} me-2`;
        iconEl.style.fontSize = '0.9rem';
        
        const titleEl = document.createElement('span');
        titleEl.textContent = arg.event.title;
        titleEl.style.fontWeight = '600';
        
        arrayOfDomNodes.push(iconEl);
        arrayOfDomNodes.push(titleEl);
        
        return { domNodes: arrayOfDomNodes };
      } else {
        // Month/Day view rendering - compact with title on new line
        const arrayOfDomNodes = [];
        
        // Container
        const containerEl = document.createElement('div');
        containerEl.className = 'event-dot-wrapper';
        
        // Top row: Icon + Time
        const topRowEl = document.createElement('div');
        topRowEl.className = 'event-top-row';
        
        // Icon
        const iconEl = document.createElement('i');
        iconEl.className = `bi ${icon}`;
        
        // Time badge
        const timeEl = document.createElement('span');
        timeEl.className = 'event-dot-time';
        const eventDate = new Date(arg.event.start);
        timeEl.textContent = eventDate.toLocaleTimeString('id-ID', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
        
        topRowEl.appendChild(iconEl);
        topRowEl.appendChild(timeEl);
        
        // Title on new line
        const titleEl = document.createElement('div');
        titleEl.className = 'event-dot-title';
        titleEl.textContent = arg.event.title;
        titleEl.title = arg.event.title; // Tooltip for full text
        
        containerEl.appendChild(topRowEl);
        containerEl.appendChild(titleEl);
        
        arrayOfDomNodes.push(containerEl);
        
        return { domNodes: arrayOfDomNodes };
      }
    },

    handleEventClick(info) {
      const isSystem = info.event.extendedProps.isSystem;
      
      if (isSystem) {
        this.selectedEvent = {
          title: info.event.title,
          start: info.event.extendedProps?.rawDate || info.event.start,
          extendedProps: info.event.extendedProps || {}
        };
        const modalElement = document.getElementById('eventDetailModal');
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      } else {
        // Attendance event
        this.selectedAttendance = info.event.extendedProps;
        this.isEditingAttendance = true;
        this.attendanceForm = {
          id: this.selectedAttendance.id,
          attendanceDate: this.selectedAttendance.attendanceDate.split('T')[0],
          phoneNumber: this.selectedAttendance.phoneNumber,
          simLabel: this.selectedAttendance.simLabel,
          attendanceType: this.selectedAttendance.attendanceType,
          status: this.selectedAttendance.status,
          title: this.selectedAttendance.title,
          notes: this.selectedAttendance.notes,
          metadata: typeof this.selectedAttendance.metadata === 'string' 
            ? this.selectedAttendance.metadata 
            : JSON.stringify(this.selectedAttendance.metadata)
        };
        
        const modalElement = document.getElementById('attendanceModal');
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    },

    handleDateClick(info) {
      this.isEditingAttendance = false;
      
      // Auto-fill phone number if an instance is selected
      let autoPhoneNumber = '';
      if (this.selectedInstanceId !== 'none') {
        const activeInstance = this.instances.find(i => i.instanceId === this.selectedInstanceId);
        if (activeInstance) {
          autoPhoneNumber = activeInstance.phoneNumber || '';
        }
      }

      this.attendanceForm = {
        attendanceDate: info.dateStr,
        phoneNumber: autoPhoneNumber,
        simLabel: '',
        attendanceType: 'device_login',
        status: 'active',
        title: '',
        notes: '',
        metadata: ''
      };
      
      const modalElement = document.getElementById('attendanceModal');
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    },

    async saveAttendance() {
      try {
        let response;
        const payload = { ...this.attendanceForm };
        
        if (this.isEditingAttendance) {
          response = await axios.patch(
            `${this.baseUrl}/api/attendance/${payload.id}`, 
            {
              phoneNumber: payload.phoneNumber,
              simLabel: payload.simLabel,
              attendanceType: payload.attendanceType,
              status: payload.status,
              title: payload.title,
              notes: payload.notes,
              metadata: payload.metadata
            }
          );
        } else {
          response = await axios.post(`${this.baseUrl}/api/attendance`, payload);
        }

        if (response.data.success) {
          // Close modal first
          const modalElement = document.getElementById('attendanceModal');
          const modal = bootstrap.Modal.getInstance(modalElement);
          modal.hide();
          
          // Show Premium Alert
          await this.$swal.fire({
            title: this.isEditingAttendance ? 'Absensi Diperbarui!' : 'Absensi Terekam!',
            html: `
              <div id="lottie-attendance-success" style="width:180px; height:180px; margin:0 auto;"></div>
              <p class="mt-2 text-dark fw-semibold">
                ${this.isEditingAttendance ? 'Perubahan berhasil disimpan' : 'Record absensi baru telah berhasil dicatat'} ke sistem.
              </p>
            `,
            confirmButtonText: 'Mantap!',
            confirmButtonColor: this.themeColor,
            didOpen: () => {
              lottie.loadAnimation({
                container: document.getElementById('lottie-attendance-success'),
                renderer: 'svg',
                loop: false,
                autoplay: true,
                path: '/assets/success.json'
              });
            }
          });
          
          // Refresh data
          await this.refreshAllData();
        }
      } catch (error) {
        console.error('Failed to save attendance:', error);
        this.showToast('Failed to save attendance. Please check your data.', 'error');
      }
    },

    async confirmDeleteAttendance() {
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        await this.deleteAttendance();
      }
    },

    async deleteAttendance() {
      try {
        const response = await axios.delete(
          `${this.baseUrl}/api/attendance/${this.attendanceForm.id}`
        );
        
        if (response.data.success) {
          this.showToast('Attendance deleted successfully', 'success');
          
          // Close modal
          const modalElement = document.getElementById('attendanceModal');
          const modal = bootstrap.Modal.getInstance(modalElement);
          modal.hide();
          
          // Refresh data
          await this.refreshAllData();
        }
      } catch (error) {
        console.error('Failed to delete attendance:', error);
        this.showToast('Failed to delete attendance.', 'error');
      }
    },

    formatEventDate(date) {
      if (!date) return '-';
      try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;
        
        return d.toLocaleString('id-ID', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).replace(/\./g, ':');
      } catch (e) {
        return date;
      }
    },

    formatMetadata(metadata) {
      if (!metadata) return '-';
      try {
        const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return metadata;
      }
    },

    showToast(message, icon = 'info') {
      this.$swal.fire({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        icon: icon,
        title: message
      });
    }
  }
};
</script>
